import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useMemo, useState } from 'react';
import { isClose } from 'is-close';
import { sum, omit } from 'lodash-es';
import { useT, hasValue, EComponentBaseType } from '@progress-fe/core';
import { Box, Center, Menu, Flex, Grid, Button, GridItem, IconButton, MenuButton, MenuDivider, MenuItem, MenuList } from '@chakra-ui/react';
import { Dialog, Svg } from '../../base';
import { ComponentsLists } from '../../lists';
import { Input, InputNumber, Select } from '../../inputs';
import { useJsFieldFormData } from '../../../hooks';
import { JsFormConfigContext } from '../../../contexts';
import { JsFieldName, JsFieldError, JsFieldInspector } from '../../jsFormBase';
/**
 * Component renders "/schemas/jsf-flow-composition" field of JsonSchema.
 * @param props : field props come from JsonSchema.
 * Value of props.formData is an instance of TFormData.
 */
const FlowCompositionJsFieldFC = (props) => {
    const [isDialog, setIsDialog] = useState(false);
    const context = useContext(JsFormConfigContext) ?? {};
    const { components, addToFavorites, removeFromFavorites, runCallback, isInspector } = context;
    const { pureList = [], lumpList = [], pseudoList = [] } = components ?? {};
    const { favorites = [], dictionary = {} } = components ?? {};
    const { t } = useT();
    const formData = useMemo(() => (hasValue(props.formData) ? props.formData : null), [props.formData]);
    const { fieldConfig, fieldValues, onChangeFieldValues, onChangeFieldConfig } = useJsFieldFormData({
        config: formData?.fieldConfig ?? null,
        values: formData?.fieldValues ?? null,
        onChange: props.onChange,
        watchPropsChanges: true
    });
    useEffect(() => {
        onChangeFieldConfig(formData?.fieldConfig);
    }, [formData?.fieldConfig, onChangeFieldConfig]);
    const getComponentNameByUuid = (uuid) => {
        return dictionary[uuid]?.name ?? pseudoList.find((i) => i.uuid === uuid)?.name ?? '';
    };
    const selectedComponentsIds = useMemo(() => {
        return fieldValues ? Object.keys(fieldValues.compositionList[0]?.composition ?? {}) : [];
    }, [fieldValues]);
    const selectedPhase = useMemo(() => {
        return fieldConfig?.phaseOptions.find((opt) => opt.value === fieldValues?.phaseValue);
    }, [fieldConfig, fieldValues]);
    const selectedUom = useMemo(() => {
        return fieldConfig?.uomOptions.find((opt) => opt.value === fieldValues?.uomValue);
    }, [fieldConfig, fieldValues]);
    const selectedComposition = useMemo(() => {
        const { compositionList, phaseValue, uomValue } = fieldValues ?? {};
        return compositionList?.find((c) => c.phase === phaseValue && c.uom === uomValue);
    }, [fieldValues]);
    const isFieldInvalid = !!props.rawErrors;
    const hasComponents = !!selectedComponentsIds.length;
    const sumOfComponents = useMemo(() => {
        if (!selectedComposition) {
            return undefined;
        }
        const valuesArray = Object.values(selectedComposition.composition);
        if (valuesArray.every((v) => v === null)) {
            return undefined;
        }
        const sumV = sum(valuesArray.map((i) => (hasValue(i) ? Number(i) : undefined)));
        const isCloseToOne = !!fieldConfig?.normalization.possible && isClose(1.0, sumV, 0.0000000001);
        return sumV === 1 || isCloseToOne ? 1 : sumV;
    }, [fieldConfig?.normalization.possible, selectedComposition]);
    const onChangePhaseValue = (option) => {
        if (fieldValues) {
            onChangeFieldValues({ ...fieldValues, phaseValue: option.value });
        }
    };
    const onChangeUomValue = (option) => {
        if (fieldValues) {
            onChangeFieldValues({ ...fieldValues, uomValue: option.value });
        }
    };
    const onChangeCompositionItem = (item) => {
        if (fieldValues) {
            const items = fieldValues.compositionList.filter((c) => !(c.phase === item.phase && c.uom === item.uom));
            onChangeFieldValues({ ...fieldValues, compositionList: [...items, item] });
        }
    };
    const onChangeCompositionList = (items) => {
        if (fieldValues) {
            onChangeFieldValues({ ...fieldValues, compositionList: items });
        }
    };
    const handleAddComponent = (key) => {
        if (fieldValues) {
            const compositionList = fieldValues.compositionList.map((item) => ({
                ...item,
                composition: { ...item.composition, [key]: null }
            }));
            onChangeCompositionList(compositionList);
        }
    };
    const handleAddComponents = (keys) => {
        if (fieldValues) {
            const compositionList = fieldValues.compositionList.map((item) => {
                let newComposition = { ...item.composition };
                keys.forEach((key) => {
                    if (!newComposition[key]) {
                        newComposition = { ...newComposition, [key]: null };
                    }
                });
                return { ...item, composition: newComposition };
            });
            onChangeCompositionList(compositionList);
        }
    };
    const handleDeleteComponent = (key) => {
        if (fieldValues) {
            const compositionList = fieldValues.compositionList.map((item) => ({
                ...item,
                composition: { ...omit(item.composition, [key]) }
            }));
            onChangeCompositionList(compositionList);
        }
    };
    const handleDeleteComponents = (keys) => {
        if (fieldValues) {
            const compositionList = fieldValues.compositionList.map((item) => {
                let newComposition = { ...item.composition };
                keys.forEach((key) => {
                    newComposition = { ...omit(newComposition, [key]) };
                });
                return { ...item, composition: newComposition };
            });
            onChangeCompositionList(compositionList);
        }
    };
    const handleChangeValue = (key, value) => {
        if (selectedComposition) {
            const compositionItem = {
                ...selectedComposition,
                composition: {
                    ...selectedComposition.composition,
                    [key]: value
                }
            };
            onChangeCompositionItem(compositionItem);
        }
    };
    const handleClearValues = () => {
        if (selectedComposition) {
            const compositionItem = {
                ...selectedComposition,
                composition: Object.keys(selectedComposition.composition).reduce((acc, curr) => ({ ...acc, [curr]: null }), {})
            };
            onChangeCompositionItem(compositionItem);
        }
    };
    const handleNormalizeCallback = async () => {
        const callbackCode = fieldConfig?.normalization.callbackCode ?? '';
        const formDataResult = await runCallback?.(callbackCode);
        if (hasValue(formDataResult)) {
            onChangeFieldConfig(formDataResult.fieldConfig);
            onChangeFieldValues(formDataResult.fieldValues);
        }
    };
    const tabs = useMemo(() => {
        return [
            {
                type: null,
                label: t(`common.favorites`),
                components: favorites
            },
            {
                type: EComponentBaseType.PURE,
                label: t(`enum.componentBaseType.${EComponentBaseType.PURE}`),
                components: pureList
            },
            {
                type: EComponentBaseType.HYPOTHETICAL,
                label: t(`enum.componentBaseType.${EComponentBaseType.HYPOTHETICAL}`),
                components: []
            },
            {
                type: EComponentBaseType.PSEUDO,
                label: t(`enum.componentBaseType.${EComponentBaseType.PSEUDO}`),
                components: pseudoList
            }
        ];
    }, [favorites, pureList, pseudoList, t]);
    if (!fieldConfig || !fieldValues?.uomValue || !fieldValues?.phaseValue) {
        return _jsx(JsFieldError, { propName: props.name });
    }
    return (_jsx(JsFieldInspector, { isActive: isInspector, fieldCode: props.name, fieldType: props.uiSchema?.['ui:field']?.toString() ?? '', valueType: ['object'], children: _jsxs(Box, { "data-testid": "FlowCompositionJsField-test", "data-name": props.name, children: [_jsxs(Flex, { p: "4px 0 2px 0", flexDirection: "column", gap: "8px", children: [_jsxs(Grid, { gridTemplateColumns: `minmax(0, 184px) 1fr`, alignItems: 'center', children: [_jsx(JsFieldName, { name: t('jsfields.FlowComposition.phase') }), _jsx(Select, { name: props.name, value: selectedPhase, options: fieldConfig.phaseOptions, placeholder: t('common.notSelected'), isDisabled: props.schema.readOnly, onChange: (newValue) => onChangePhaseValue(newValue) })] }), _jsxs(Grid, { gridTemplateColumns: `minmax(0, 184px) 1fr`, alignItems: 'center', children: [_jsx(JsFieldName, { name: t('jsfields.FlowComposition.uom') }), _jsx(Select, { name: props.name, value: selectedUom, options: fieldConfig.uomOptions, placeholder: t('common.notSelected'), isDisabled: props.schema.readOnly, onChange: (newValue) => onChangeUomValue(newValue) })] }), hasComponents && !!selectedComposition ? (_jsxs(GridItem, { p: "4px", borderRadius: "4px", border: "1px solid", borderColor: isFieldInvalid ? 'error' : 'border', children: [_jsxs(Grid, { gap: "4px", p: "0 0 4px 0", alignItems: "center", borderBottom: "1px solid", borderColor: "border", templateColumns: "176px 1fr 24px", children: [_jsx(GridItem, { children: t('fields.component') }), _jsx(GridItem, { children: t('fields.amount') }), _jsx(GridItem, { children: _jsx(IconButton, { size: "smSquare", "aria-label": "", variant: "ghostTr", icon: _jsx(Svg, { size: "s12", name: "Cross" }), isDisabled: props.schema.readOnly || fieldConfig?.readOnly, onClick: () => handleClearValues() }) })] }), _jsx(Box, { p: "2px 0", children: selectedComponentsIds.map((uuid) => {
                                        const isLump = lumpList.some((i) => i.uuid === uuid);
                                        return (_jsxs(Grid, { gap: "4px", p: "2px 0", templateColumns: "176px 1fr 24px", children: [_jsx(Flex, { align: "center", className: 'cut-text', children: getComponentNameByUuid(uuid) }), _jsx(InputNumber, { isFloat: true, min: 0, size: "xs", variant: "outline", sx: { width: '100%' }, isOnChangeOnlyOnBlur: true, disabled: isLump || props.schema.readOnly || fieldConfig?.readOnly, rightElement: selectedUom?.unit, isInvalid: !hasValue(selectedComposition.composition[uuid]), value: selectedComposition.composition[uuid] || undefined, onChange: (value) => handleChangeValue(uuid, value) }), _jsx(IconButton, { size: "smSquare", "aria-label": "", variant: "ghost", isDisabled: isLump || props.schema.readOnly || fieldConfig?.readOnly, icon: _jsx(Svg, { size: "s12", name: "Cross" }), onClick: () => handleDeleteComponent(uuid) })] }, uuid));
                                    }) }), _jsxs(Grid, { gap: "4px", p: "4px 0 0 0", borderTop: "1px solid", borderColor: "border", templateColumns: "176px 1fr auto", children: [_jsx(Flex, { align: "center", children: t('common.total') }), _jsx(Input, { size: "xs", isDisabled: true, variant: "outline", value: sumOfComponents, isInvalid: fieldConfig.normalization.possible && sumOfComponents !== 1 }), fieldConfig.normalization.possible && (_jsx(Button, { size: "xs", variant: "ghost", isDisabled: props.schema.readOnly || fieldConfig?.readOnly, onClick: handleNormalizeCallback, children: t('actions.normalize') }))] })] })) : (_jsx(Center, { h: "32px", color: "bodyText", border: "1px solid", borderColor: isFieldInvalid ? 'error' : 'border', borderRadius: "4px", children: _jsx(Box, { opacity: "0.6", children: t('components.no') }) })), !props.schema.readOnly && !fieldConfig?.readOnly && (_jsx(GridItem, { children: _jsxs(Menu, { matchWidth: true, offset: [0, 2], variant: "outline", children: [_jsx(MenuButton, { size: "xs", as: Button, width: "100%", variant: "ghost", onClick: () => setIsDialog(false), isDisabled: !selectedComposition, children: t('actions.add') }), _jsxs(MenuList, { motionProps: { animate: false }, zIndex: 2, children: [favorites.map((item) => (_jsx(MenuItem, { isDisabled: selectedComponentsIds.includes(item.uuid), onClick: () => handleAddComponent(item.uuid), children: item.name }, item.uuid))), favorites.length === 0 && (_jsx(MenuItem, { isDisabled: true, children: t('components.noFavorite') })), _jsx(MenuDivider, {}), _jsx(MenuItem, { onClick: () => setIsDialog(true), children: t('components.all') })] })] }) }))] }), isDialog && (_jsx(Dialog, { title: t('components.add'), onClose: () => setIsDialog(false), children: _jsx(GridItem, { pt: "8px", children: _jsx(ComponentsLists, { tabs: tabs, tableSx: { height: 500 }, selectedComponentsIds: selectedComponentsIds, onAddComponent: handleAddComponent, onDeleteComponent: handleDeleteComponent, onAddAllComponents: handleAddComponents, onDeleteAllComponents: handleDeleteComponents, onAddToFavorite: (item) => addToFavorites?.([item]), onDeleteFromFavorite: (item) => removeFromFavorites?.([item]), onClose: () => setIsDialog(false) }) }) }))] }) }));
};
export const FlowCompositionJsField = FlowCompositionJsFieldFC;
//# sourceMappingURL=FlowCompositionJsField.js.map